'use client';
import {
  Box,
  IconButton,
  Input,
  Stack,
  Link,
  VStack,
  HStack,
  Icon,
  Text,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import {
  DialogRoot,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogTrigger,
  DialogBackdrop,
  DialogCloseTrigger,
  DialogContent,
} from '../ui/dialog';
import { AiTwotoneCloseCircle } from 'react-icons/ai';
import { useTranslations } from 'next-intl';
import { requestLogin } from '@/lib/services/user.service';
import { Button } from '../ui/button';
import { MD5 } from '@/lib/utils/crypto';
import { toaster } from '@/components/ui/toaster';
import { useAppDispatch, useAppSelector } from '@/lib/hooks/storeHook';
import {
  selectEmailLoginOpen,
  setEmailLoginOpen,
  setEmailRegisterOpen,
} from '@/lib/stores/uiSlice';
import { BiSolidHide, BiSolidShow } from 'react-icons/bi';
import * as z from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

const MIN_PASSWORD_LENGTH = 8;
type Props = {
  onLogin: () => void;
};

export function EmailLogin({ onLogin }: Props): JSX.Element {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [googleAuth, setGoogleAuth] = useState('');
  const t = useTranslations('Navigation.emailRegister');
  const tAuth = useTranslations('Auth');
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const emailLoginOpen = useAppSelector(selectEmailLoginOpen);

  const signUpSchema = useMemo(
    () =>
      z.object({
        email: z.string().email(tAuth('validation.emailInvalid')),
        password: z
          .string()
          .min(MIN_PASSWORD_LENGTH, tAuth('validation.passwordLength'))
          .regex(/\d/, tAuth('validation.passwordNumber'))
          .regex(/[A-Z]/, tAuth('validation.passwordUppercase')),
        googleAuth: z.string().optional(),
      }),
    [tAuth],
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    setValue,
    getValues,
  } = useForm<z.infer<typeof signUpSchema>>({
    resolver: zodResolver(signUpSchema as any),
    mode: 'onChange',
    defaultValues: {
      email: '',
      password: '',
      googleAuth: '',
    },
  });

  const login = async (data: z.infer<typeof signUpSchema>) => {
    setLoading(true);
    try {
      await requestLogin(data.email, MD5(data.password), data.googleAuth ?? '');
      onLogin();
    } catch (error: any) {
      toaster.create({
        title: error.message,
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = handleSubmit(login);

  return (
    <DialogRoot
      placement={'center'}
      open={emailLoginOpen}
      onOpenChange={(e) => dispatch(setEmailLoginOpen(e.open))}
    >
      <DialogBackdrop />
      <DialogTrigger asChild>
        <Button
          px={6}
          py={2}
          h="auto"
          borderRadius="100px"
          bg="text.t1"
          color="bg.t1"
          _hover={{
            opacity: 0.8,
          }}
        >
          {t('login')}
        </Button>
      </DialogTrigger>

      <DialogContent maxW="480px" width="100%" borderRadius="20px">
        <DialogHeader>
          <DialogTitle color="text.t1" pt={5} pb={3} px={5}>
            {t('login')}
          </DialogTitle>
          <DialogCloseTrigger>
            <Icon fontSize="24px">
              <AiTwotoneCloseCircle />
            </Icon>
          </DialogCloseTrigger>
        </DialogHeader>

        <DialogBody>
          <Stack
            width="480px"
            bg="bg.t1"
            p={8}
            borderRadius="xl"
            position="relative"
            gap={4}
          >
            {/* Email Input */}
            <VStack w="full" align="start" gap={1}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder={t('email')}
                    size="lg"
                    bg="transparent"
                    border="1px solid"
                    borderColor="text.t4"
                    rounded={8}
                    variant="outline"
                    py={'6px'}
                    px={3}
                  />
                )}
              />
              {errors.email && (
                <Text fontSize="11px" color="red.500" alignSelf="start">
                  {errors.email.message}
                </Text>
              )}
            </VStack>

            {/* Password Input with Toggle Icon */}
            <VStack w="full" align="start" gap={1} position="relative">
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('password')}
                    size="lg"
                    border="1px solid"
                    borderColor="text.t4"
                    rounded={8}
                    py={'6px'}
                    px={3}
                  />
                )}
              />
              {errors.password ? (
                <Text fontSize="11px" color="red.500" alignSelf="start">
                  {errors.password.message}
                </Text>
              ) : (
                <Text fontSize="11px" color="text.t3" alignSelf="start">
                  {t('passwordRules')}
                </Text>
              )}
              <IconButton
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                variant="ghost"
                position="absolute"
                right={2}
                top={1}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <BiSolidHide /> : <BiSolidShow />}
              </IconButton>
            </VStack>

            {/* Google auth Input */}
            <Controller
              control={control}
              name="googleAuth"
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder={t('googleAuth')}
                  size="lg"
                  variant="outline"
                  borderRadius="8px"
                  border="1px solid"
                  borderColor="text.t4"
                  px="12px"
                  py="6px"
                />
              )}
            />
          </Stack>
        </DialogBody>

        <DialogFooter bg="bg.t1" borderRadius="20px" overflow="hidden">
          {/* Submit Button */}
          <VStack width="full" pb={5} bg="bg.t1">
            <Button
              loading={loading}
              // TODO: add i18n loading text
              // loadingText="Logging in..."
              size="lg"
              bg="text.t1"
              color="bg.t1"
              borderRadius="99px"
              width="440px"
              _hover={{
                opacity: 0.8,
              }}
              onClick={onSubmit}
            >
              {t('login')}
            </Button>

            {/* Resend Link */}
            <HStack px={5} width="full" justify="end">
              {/* <Link color="text.primary" textAlign="center" fontSize="sm">
                {t('forgotPassword')}
              </Link> */}
              <Text
                color="text.primary"
                textAlign="center"
                fontSize="sm"
                cursor="pointer"
                onClick={() => {
                  dispatch(setEmailRegisterOpen(true));
                  dispatch(setEmailLoginOpen(false));
                }}
              >
                {t('signup')}
              </Text>
            </HStack>
          </VStack>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
